import { AgGridColumnProps } from "ag-grid-react";
import { ICrmSpecifications } from "../../@types/crmSpecifications";
import moment from "moment-timezone";
import momentDurationFormatSetup from "moment-duration-format";
import "moment/locale/he"; // without this line, language is not imported
import {
  DateFilterParams,
  NumberComparator,
  StringFilterParams
} from "utils/Haim/agGridCellRenderers";
import UploadExplanationGeneric from "components/__haim/ImportLeads/UploadExplanationGeneric";
import { CallSummary } from "../../@types/sharedSchema";
import { getAudioUrlFromCallSummary } from "utils/Haim/utils";
import { textIncludes } from "utils/table";

momentDurationFormatSetup(moment as any);

export class GenericCrmSpecifications implements ICrmSpecifications {
  getDefaultDataColumns(): AgGridColumnProps[] {
    return generiCrmColumns;
  }

  getLeadsImportUISection() {
    return <UploadExplanationGeneric />;
  }

  runLeadsImportValidations(parsedRow: any, rawRow: any) {
    return parsedRow;
  }

  getLeadsImportDemoFileName() {
    return "/static/_haim/demoImportFileGeneric.xlsx";
  }
}

export const optedOutOfBulkMessagesPossibleValues = ["", false, true];

export const isInSpamDatabasePossibleValues = ["", false, true];

export const phoneNumberCol: AgGridColumnProps = {
  colId: "phoneNumber",
  field: "id",
  headerName: "מספר טלפון",
  cellStyle: { "font-weight": "bold", direction: "ltr", "text-align": "center" },
  filter: true,
  filterParams: StringFilterParams
};

export const metadataParsedCallerPhoneNumberCol: AgGridColumnProps = {
  filter: true,
  colId: "metadataParsedCallerPhoneNumber",
  headerName: "מספר טלפון",
  filterParams: StringFilterParams,
  cellRenderer: "PhoneNumberCellRenderer",
  // Using cellRendererParams for actual value display
  cellRendererParams: (params) => ({
    paramsPhoneNumber: params.data._metadata?.callerPhoneNumber ?? params.data.id,
    textStyle: {
      fontWeight: "bold"
    }
  }),
  getQuickFilterText: (params) =>
    params.data._metadata?.callerPhoneNumber?.formatNational?.().replace(/\D/g, "") ??
    params.data.id,
  // Using value getter for filter values + csv export, since there is no dedicated value getter for csv
  valueGetter: (params) =>
    params.data._metadata?.callerPhoneNumber?.formatInternational?.() ?? params.data.id
};

export const tagsCol: AgGridColumnProps = {
  colId: "tags",
  field: "tags",
  headerName: "תגיות",
  filter: true,
  minWidth: 300,
  cellRenderer: "TagsRenderer",
  valueGetter: ({ data: { tags } }) => {
    return JSON.stringify(tags);
  },
  filterParams: {
    buttons: ["clear"],
    filterOptions: ["contains"],
    suppressAndOrCondition: true,
    textCustomComparator: (_filterOption: string, columnValue: string, filterText: string) => {
      const splittedFilterValue = filterText.trim().split(" ").filter(Boolean);

      if (!splittedFilterValue.length) return true;

      const tags = JSON.parse(columnValue);

      if (!tags?.length) return false;

      const parsedColumnValue = tags.join(", ");

      let isMatch = false;

      for (const filterValue of splittedFilterValue) {
        isMatch = textIncludes(parsedColumnValue, filterValue);

        if (isMatch) break;
      }

      return isMatch;
    }
  }
};

export const callerFirstNameCol: AgGridColumnProps = {
  colId: "callerFirstName",
  field: "callerFirstName",
  filter: true,
  editable: true,
  headerName: "שם פרטי",
  filterParams: StringFilterParams
};

export const callerLastNameCol: AgGridColumnProps = {
  colId: "callerLastName",
  field: "callerLastName",
  headerName: "שם משפחה",
  filter: true,
  filterParams: StringFilterParams,
  editable: true
};

export const createdTimeCol: AgGridColumnProps = {
  colId: "createdTime",
  field: "createdTime",
  filter: "agDateColumnFilter",
  headerName: "תאריך יצירה",
  filterParams: DateFilterParams,
  cellRenderer: "DateTimeRenderer"
};

export const updatedTimeCol: AgGridColumnProps = {
  colId: "updatedTime",
  field: "updatedTime",
  filter: "agDateColumnFilter",
  headerName: "תאריך עדכון אחרון",
  filterParams: DateFilterParams,
  cellRenderer: "DateTimeRenderer"
};

export const contactOriginCol: AgGridColumnProps = {
  colId: "contactOrigin",
  field: "contactOrigin",
  filter: true,
  minWidth: 120,
  editable: true,
  headerName: "מקור ליד",
  filterParams: StringFilterParams
};

export const contactOriginAdditionalInfoCol: AgGridColumnProps = {
  field: "contactOriginAdditionalInfo",
  colId: "contactOriginAdditionalInfo",
  headerName: "מקור ליד פרטים נוספים",
  minWidth: 120,
  filter: true,
  filterParams: StringFilterParams,
  editable: true
};

export const lastBulkMessageReceivedTimeCol: AgGridColumnProps = {
  colId: "lastBulkMessageReceivedTime",
  field: "lastBulkMessageReceived.sentTime",
  headerName: "הודעה אחרונה התקבלה:",
  //filter: "agDateColumnFilter",
  filter: "lastBulkMessageReceivedFilter",
  filterParams: DateFilterParams,
  cellRenderer: "DateTimeRenderer"
};

export const optedOutOfBulkMessagesCol: AgGridColumnProps = {
  colId: "optedOutOfBulkMessages",
  field: "optedOutOfBulkMessages",
  headerName: "ביקש לא לקבל דיוור",
  cellEditor: "agSelectCellEditor",
  cellEditorParams: {
    values: optedOutOfBulkMessagesPossibleValues
  },
  valueFormatter: (params) => typeof params.value === "boolean" && (params.value ? "כן" : "לא"),
  cellRenderer: "BooleanRenderer",
  cellRendererParams: () => ({
    colors: { true: "error", false: "success" }
  }),
  editable: true
};

export const isInSpamDatabaseCol: AgGridColumnProps = {
  // field: "isInSpamDatabase",
  colId: "isInSpamDatabase",
  headerName: "נמצא במאגר 'לא להתקשר אליי'",
  cellEditor: "agSelectCellEditor",
  cellEditorParams: {
    values: isInSpamDatabasePossibleValues
  },
  valueGetter: (params) => {
    if (params.data.isInSpamDatabase === true) return "כן";
    if (params.data.isInSpamDatabase === false) return "לא";
    return "?";
  },
  cellRenderer: "ColoredLabelRenderer",
  cellRendererParams: {
    colors: {
      לא: "success",
      כן: "error",
      "?": "warning"
    }
  },
  editable: false
};

export const additionalNotesCol: AgGridColumnProps = {
  colId: "additionalNotes",
  field: "additionalNotes",
  headerName: "הערות נוספות",
  filter: true,
  filterParams: StringFilterParams,
  editable: true
};

export const generiCrmColumns: AgGridColumnProps[] = [
  metadataParsedCallerPhoneNumberCol,
  callerFirstNameCol,
  callerLastNameCol,
  additionalNotesCol,
  createdTimeCol,
  updatedTimeCol,
  tagsCol,
  contactOriginCol,
  lastBulkMessageReceivedTimeCol,
  optedOutOfBulkMessagesCol,
  isInSpamDatabaseCol
];

export const virtualNumberCallerNumberCol: AgGridColumnProps = {
  field: "callerNumber",
  filter: true,
  maxWidth: 150,
  cellStyle: { "font-weight": "bold" },
  headerName: "מספר נכנס",
  filterParams: StringFilterParams
};

export const virtualNumberCallDurationCol: AgGridColumnProps = {
  headerName: "משך שיחה",
  comparator: NumberComparator,
  valueGetter: (params) => {
    let row = params.data;
    let answeredCall = row["result"] === "ANSWER";
    return answeredCall ? row["duration"] : null;
  },
  valueFormatter: (params) =>
    !params.value ? "" : moment.duration(params.value, "seconds").format()
};

export const virtualNumberCallEndAtCol: AgGridColumnProps = {
  field: "endAt",
  headerName: "תאריך שיחה",
  filter: "agDateColumnFilter",
  filterParams: DateFilterParams,
  cellRenderer: "DateTimeRenderer"
};

export const virtualNumberCallResultCol: AgGridColumnProps = {
  field: "result",
  headerName: "",
  cellRenderer: "CallStatusRenderer",
  maxWidth: 80
};

export const virtualNumberCallRecordingLinkDirectCol: AgGridColumnProps = {
  minWidth: 300,
  headerName: "הקלטה",
  cellRenderer: "AudioPlayerCellRenderer",
  valueGetter: (params) => {
    let row = params.data as CallSummary;
    const audioUrl = getAudioUrlFromCallSummary(row);
    return audioUrl;
  }
};

export const virtualNumberPhoneNumberCol: AgGridColumnProps = {
  field: "virtualNumber",
  headerName: "מספר וירטואלי",
  cellStyle: { "font-weight": "bold" },
  maxWidth: 150,
  filter: true,
  filterParams: StringFilterParams
};

export const virtualNumberCallsColumns: AgGridColumnProps[] = [
  virtualNumberCallResultCol,
  metadataParsedCallerPhoneNumberCol,
  virtualNumberCallDurationCol,
  virtualNumberCallEndAtCol,
  virtualNumberCallRecordingLinkDirectCol
];

export const metadataColumnNames: string[] = [
  createdTimeCol.field,
  lastBulkMessageReceivedTimeCol.field
];
